import { isBefore } from 'date-fns';

declare global {
    interface Array<T> {
        /**
         * Helper function that can be used to filter and map in one iteration instead of chaning the calls which would
         * increases the amount of time the array has to be iterated through.
         *
         * The filter works by returning null whenever an element should be filtered out and otherwise return the type you
         * want to map to.
         */
        filteredMap<R>(map: (item: T) => R | null): R[];
    }
}

function getRandomElements<T>(array: T[], numOfElements: number): T[] {
    const copiedArray = array.slice();

    // Shuffle the array
    for (let i = copiedArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copiedArray[i], copiedArray[j]] = [copiedArray[j], copiedArray[i]];
    }

    // Take the first X elements of the shuffled array
    return copiedArray.slice(0, numOfElements);
}

type ID = string | number;
type AnyObjectWithId = {
    id: ID;
    [key: string]: any;
};

// for cases where we have an array of objects with an id property
const removeDuplicates = <T extends AnyObjectWithId>(items: T[]) => {
    return items.filter((item, index, self) => index === self.findIndex((x) => x.id === item.id));
};
const removeExactDuplicates = (items: any[]) => {
    return items.filter((item, index, self) => index === self.findIndex((x) => x === item));
};

const getChanges = <T extends AnyObjectWithId>(
    oldArray: T[],
    newArray: T[]
): { removedElements: T[]; newElements: T[] } => {
    const oldIds: ID[] = oldArray.map((item) => item.id);
    const newIds: ID[] = newArray.map((item) => item.id);

    return {
        removedElements: oldArray.filter(
            (item) => newIds.find((id) => id === item.id) === undefined
        ),
        newElements: newArray.filter((item) => oldIds.find((id) => id === item.id) === undefined)
    };
};

const notEmpty = <T>(value: T | null | undefined): value is T => {
    return value !== null && value !== undefined;
};

const getIntersection = <T extends AnyObjectWithId>(arr1: T[], arr2: T[]): T[] => {
    return arr1.filter((item1) => arr2.some((item2) => item1.id === item2.id));
};

const getNonIntersection = <T extends AnyObjectWithId>(arr1: T[], arr2: T[]): T[] => {
    return arr1.filter((item1) => !arr2.some((item2) => item1.id === item2.id));
};

const removeEmptyValues = <T>(array: (T | undefined | null)[]) => array.filter(notEmpty);

const sortByCreatedAt = <T extends { createdAt: Date }>(array: T[]) =>
    array.sort((a, b) => {
        const aBeforeB = isBefore(a.createdAt, b.createdAt);
        // we return latest first
        return aBeforeB ? 1 : -1;
    });

const sortByUpdatedAt = <T extends { updatedAt: Date }>(array: T[]) =>
    array.sort((a, b) => {
        const aBeforeB = isBefore(a.updatedAt, b.updatedAt);
        // we return latest first
        return aBeforeB ? 1 : -1;
    });

Array.prototype.filteredMap = function <T, R>(map: (value: T) => R | null): R[] {
    return this.reduce<R[]>((accumulator, currentValue) => {
        const mappedValue = map(currentValue);
        if (mappedValue != null) {
            accumulator.push(mappedValue);
        }
        return accumulator;
    }, []);
};

export {
    getRandomElements,
    getChanges,
    removeDuplicates,
    removeExactDuplicates,
    notEmpty,
    removeEmptyValues,
    getIntersection,
    getNonIntersection,
    sortByCreatedAt,
    sortByUpdatedAt
};
